// Single source for every economy figure that appears in PUBLIC COPY.
//
// Why this file exists: the fee was published as 1%, 1-2%, 1-5% and a tiered
// 0.5%/1-2%/3-5% across five surfaces, and a reviewer used the contradiction as
// evidence the platform doesn't know how it works. Any page that prints a fee or
// bonus figure imports from here; none of them type a number into JSX.
//
// The MONTHLY maintenance fee is NOT redefined here — it already has a single
// definition in `./fees.ts` and is re-exported below so there is still one number.
//
// The live current rate is authoritative where a component can fetch it:
// `useGetEconomicMetricsQuery()` (public, 5-min cache) returns
// `transactionFeePercentage`. These constants describe the RANGE, which is a
// policy statement and not a live value, and serve as the copy fallback.
export { MONTHLY_MAINTENANCE_FEE_POINTS } from './fees';

/** Lower bound of the published transaction-fee range, in percent. */
export const FEE_RATE_MIN_PCT = 1;

/** Upper bound of the published transaction-fee range, in percent. */
export const FEE_RATE_MAX_PCT = 5;

/** Points per US dollar. Confirmed against the backend trade path. */
export const POINTS_PER_USD = 100;

/**
 * The fee range as it appears in prose, e.g. "between 1% and 5%".
 *
 * Use this on surfaces that CANNOT fetch the live rate — static SEO copy, meta
 * descriptions, Open Graph. A bare single percentage is not permitted anywhere,
 * because the rate moves and a fixed number goes stale silently.
 */
export function formatFeeRangeCopy(): string {
  return `between ${FEE_RATE_MIN_PCT}% and ${FEE_RATE_MAX_PCT}%`;
}

/**
 * The canonical fee sentence for surfaces that HAVE the live rate.
 *
 * Pass `transactionFeePercentage` from `useGetEconomicMetricsQuery()`. When the
 * fetch hasn't resolved, pass `undefined` and the sentence degrades to the range
 * alone rather than inventing a current rate.
 */
export function formatFeeSentence(currentPct?: number): string {
  const range = `It varies ${formatFeeRangeCopy()} based on the health of the points economy, and can drop to 0% when the economy needs points.`;
  return currentPct == null
    ? `The transaction fee varies ${formatFeeRangeCopy()} based on the health of the points economy, and can drop to 0% when the economy needs points.`
    : `The transaction fee is currently ${formatFeePct(currentPct)}. ${range}`;
}

/**
 * A fee rate as it appears in copy: whole numbers without decimals ("2%"),
 * anything else to one decimal ("1.5%").
 */
export function formatFeePct(ratePct: number): string {
  return Number.isInteger(ratePct) ? `${ratePct}%` : `${ratePct.toFixed(1)}%`;
}

/**
 * The fee on an example trade, in points, rounded down the way settlement
 * rounds it ("Fees are always rounded down", admin controls copy). Copy that
 * quotes "on a 1,000-point trade that is N points" computes N with this.
 */
export function feeExample(points: number, ratePct: number): number {
  return Math.floor((points * ratePct) / 100);
}

/**
 * What an Elite member pays, from the live rate and the Elite discount. At a
 * 100% discount it is "no transaction fee"; at any other discount the copy
 * says how much of which fee is taken off, so it survives a change to either.
 */
export function eliteFeeCopy(ratePct: number, discountPct: number): string {
  if (discountPct >= 100) return 'no transaction fee';
  return `${formatFeePct(discountPct)} off the ${formatFeePct(ratePct)} fee`;
}
